/*
  gcseo-track.js — the browser half of the GENTCREATE order tracker (sections/gcseo-track.liquid).

  Mirrors scripts/gc_track_sync.py (scheme v2) exactly:
    identifier  "o|<ORDER>|<email>"  (full record)  or  "t|<TRACKING>"  (milestones only)
    dk          PBKDF2-SHA256(identifier, salt "gct2|gentcreate", 300000 rounds) -> 52 bytes
    key         dk[0:32]  AES-256-GCM
    handle      hex(dk[32:52])  the metaobject handle, so even testing a guess costs the KDF
    blob        base64(iv[12] || ciphertext), additional data = handle
  The record is fetched from /search?q=<handle>&view=gcseo-track and decrypted here; every value is
  written with textContent (never innerHTML). Nothing is stored in the browser, and no order or
  tracking number is ever put in a URL or sent to analytics.
*/
(function () {
  'use strict';
  var root = document.querySelector('[data-gcseo-track]');
  if (!root) return;
  var out = root.querySelector('[data-out]');

  var T = {};
  try { T = JSON.parse(document.querySelector('[data-gcseo-track-i18n]').textContent); } catch (e) { T = {}; }
  T.head = T.head || {}; T.sub = T.sub || {}; T.note = T.note || {}; T.spot = T.spot || {};
  // No WebCrypto (very old browser, or a page not served over https): say so instead of a dead form.
  // The buttons stay disabled, so nothing is ever submitted natively.
  if (!window.crypto || !window.crypto.subtle || !window.TextEncoder || !window.TextDecoder || !window.fetch) {
    if (out && T.unsupported) {
      var p0 = document.createElement('p');
      p0.className = 'gcseo-trk-msg gcseo-trk-msg--warn';
      p0.textContent = T.unsupported + ' ' + (T.supportEmail || '');
      out.appendChild(p0);
    }
    return;
  }
  var SALT = 'gct2|gentcreate', ROUNDS = 300000, TIMEOUT = 12000;
  var enc = new TextEncoder();
  var locale = root.getAttribute('data-locale') || document.documentElement.lang || 'en';
  var RANK = ['preparing', 'packed', 'transit', 'out', 'returned', 'delivered'];

  // ---------------------------------------------------------------- normalisation (must match Python)
  function normOrder(s) { return String(s || '').toUpperCase().replace(/[#\s]/g, ''); }
  function normEmail(s) { return String(s || '').trim().toLowerCase(); }
  function normTracking(s) { return String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }

  // ---------------------------------------------------------------- crypto
  function hex(buf) {
    var b = new Uint8Array(buf), s = '';
    for (var i = 0; i < b.length; i++) s += (b[i] < 16 ? '0' : '') + b[i].toString(16);
    return s;
  }
  function derive(identifier) {
    return crypto.subtle.importKey('raw', enc.encode(identifier), 'PBKDF2', false, ['deriveBits']).then(function (base) {
      return crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: enc.encode(SALT), iterations: ROUNDS }, base, 416);
    }).then(function (bits) {
      var dk = new Uint8Array(bits), handle = hex(dk.slice(32, 52));
      return crypto.subtle.importKey('raw', dk.slice(0, 32), { name: 'AES-GCM' }, false, ['decrypt'])
        .then(function (key) { return { key: key, handle: handle }; });
    });
  }
  function unseal(k, blob) {
    var raw = Uint8Array.from(atob(blob), function (c) { return c.charCodeAt(0); });
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv: raw.slice(0, 12), additionalData: enc.encode(k.handle) }, k.key, raw.slice(12))
      .then(function (pt) { return JSON.parse(new TextDecoder().decode(pt)); });
  }

  // ---------------------------------------------------------------- lookup
  function lookup(identifier) {
    return derive(identifier).then(function (k) {
      var url = (root.getAttribute('data-lookup') || '/search') + '?q=' + k.handle + '&view=' + (root.getAttribute('data-view') || 'gcseo-track') + '&type=page&options%5Bprefix%5D=none';
      var pv = new URLSearchParams(location.search).get('preview_theme_id');
      if (pv && /^\d+$/.test(pv)) url += '&preview_theme_id=' + pv;
      // One retry: a storefront bot check or a cold cache can answer the first request with HTML.
      // Each attempt is cut off after TIMEOUT (headers and body), so a stalled mobile connection
      // ends in the error message instead of an endless spinner.
      function get(attempt) {
        var c = window.AbortController ? new AbortController() : null;
        var tm = c ? setTimeout(function () { c.abort(); }, TIMEOUT) : null;
        return fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: c ? c.signal : undefined, headers: { Accept: 'application/json, text/plain, */*' } })
          .then(function (r) { if (!r.ok) throw new Error('http'); return r.text(); })
          .then(function (txt) { clearTimeout(tm); return JSON.parse(txt.trim()); }, function (err) { clearTimeout(tm); throw err; })
          .catch(function (err) {
            if (attempt > 0) throw err;
            return new Promise(function (res) { setTimeout(res, 1200); }).then(function () { return get(attempt + 1); });
          });
      }
      return get(0).then(function (d) {
        if (!d.found) return null;
        return unseal(k, d.b);
      });
    });
  }

  // ---------------------------------------------------------------- dates and words
  function toDate(iso) { var p = String(iso).split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  var fmtDay;
  try { fmtDay = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch (e) { fmtDay = new Intl.DateTimeFormat('en', { weekday: 'short', day: 'numeric', month: 'short' }); }
  function fd(iso) { return iso ? fmtDay.format(toDate(iso)).replace(/ /g, '\u00a0') : ''; }   // a date never breaks across lines
  function range(r) { if (!r || !r[0]) return ''; return r[0] === r[1] ? fd(r[0]) : fd(r[0]) + ' – ' + fd(r[1]); }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  function count(n, one, many) { return n === 1 ? fill(one, { n: n }) : fill(many, { n: n }); }

  // ---------------------------------------------------------------- DOM helpers (textContent only)
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null && text !== '') n.textContent = text;
    return n;
  }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }
  function line(parent, cls, text) { if (text) parent.appendChild(el('p', cls, text)); }

  function message(text, kind) {
    clear(out);
    var box = el('div', 'gcseo-trk-msg' + (kind ? ' gcseo-trk-msg--' + kind : ''));
    box.appendChild(el('p', null, text));
    out.appendChild(box);
  }

  function loading() {
    clear(out);
    var box = el('div', 'gcseo-trk-msg gcseo-trk-msg--loading');
    box.appendChild(el('span', 'gcseo-trk-spin'));
    box.appendChild(el('p', null, T.loading || 'Finding your order'));
    out.appendChild(box);
  }

  // ---------------------------------------------------------------- pieces of the result
  function steps(p, placed) {
    var st = p ? p.state : 'preparing';
    var done = [true, !!(p && p.dispatched), !!(p && p.shipped), st === 'out' || st === 'delivered', st === 'delivered'];
    var dates = [placed, p && p.dispatched, p && p.shipped, p && p.out, p && p.delivered];
    var now = st === 'returned' || (p && shown(p) === 'shipped') ? -1 : done.indexOf(false);
    var ol = el('ol', 'gcseo-trk-steps');
    (T.steps || ['Placed', 'Prepared', 'Shipped', 'On its way', 'Delivered']).forEach(function (label, i) {
      var li = el('li', 'gcseo-trk-step' + (done[i] ? ' is-done' : '') + (i === now ? ' is-now' : ''));
      if (i === now) li.setAttribute('aria-current', 'step');
      li.appendChild(el('span', 'gcseo-trk-step__dot'));
      li.appendChild(el('span', 'gcseo-trk-step__label', i === 3 && st === 'out' ? T.head.out || label : label));
      if (done[i] && dates[i]) li.appendChild(el('span', 'gcseo-trk-step__date', fd(dates[i])));
      ol.appendChild(li);
    });
    return ol;
  }

  function itemsList(items, idxs) {
    var ul = el('ul', 'gcseo-trk-items');
    (idxs || items.map(function (_, i) { return i; })).forEach(function (i) {
      var it = items[i];
      if (!it) return;
      var li = el('li', 'gcseo-trk-item');
      if (it.img && /^https:\/\//.test(it.img)) {
        var img = document.createElement('img');
        img.src = it.img; img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64; img.decoding = 'async';
        li.appendChild(img);
      } else {
        li.appendChild(el('span', 'gcseo-trk-item__ph'));
      }
      var txt = el('div', 'gcseo-trk-item__txt');
      txt.appendChild(el('p', 'gcseo-trk-item__t', it.t));
      line(txt, 'gcseo-trk-item__v', it.v);
      if (it.init) txt.appendChild(el('p', 'gcseo-trk-item__init', fill(T.initials || 'Initials: [text]', { text: it.init })));
      li.appendChild(txt);
      if (it.q > 1) li.appendChild(el('span', 'gcseo-trk-item__q', (T.qty || 'Qty') + ' ' + it.q));
      ul.appendChild(li);
    });
    return ul;
  }

  function etaLine(p) {
    if (!p || !p.eta || p.state === 'delivered' || p.state === 'returned') return null;
    if (p.state === 'out') return p.out_window ? fill(T.outWindow || 'Expected today [window]', { window: p.out_window }) : null;
    if (p.eta_src === 'carrier') return fill(T.etaCarrier || 'Expected by [date]', { date: fd(p.eta[1]) });
    if (p.eta_src === 'typical') return fill(T.eta || 'Estimated delivery [range]', { range: range(p.eta) });
    return null;   // passed: the note says what to do
  }

  function deliveredLine(p) {
    if (!p || !p.delivered) return '';
    var s = fill(T.sub.delivered, { date: fd(p.delivered) });
    if (p.spot && T.spot[p.spot]) s += ' ' + T.spot[p.spot];
    return s;
  }

  function noteFor(p) {
    if (!p || !p.note) return null;
    if (p.note === 'passed') {
      var tpl = p.untracked ? (T.note.passedUntracked || T.note.passed) : T.note.passed;
      return p.eta ? fill(tpl, { date: fd(p.eta[1]) }) : null;
    }
    return T.note[p.note] || null;
  }

  function byRank(a, b) { return RANK.indexOf(a.state) - RANK.indexOf(b.state); }
  // A parcel on a route that never reports a scan, past its usual window: most of these arrived long
  // ago, so it reads "Shipped on <date>" rather than "On its way" (and is never claimed as delivered).
  function shown(p) { return p.state === 'transit' && p.untracked && p.note === 'passed' ? 'shipped' : p.state; }

  // ---------------------------------------------------------------- render
  function render(rec, byTracking) {
    clear(out);
    var parcels = rec.parcels || [], items = rec.items || [];
    var unshipped = rec.unshipped || [];
    var lead = parcels.slice().sort(byRank)[0] || null;
    var state = rec.state === 'cancelled' ? 'cancelled' : lead ? shown(lead) : (rec.overdue ? 'overdue' : 'preparing');

    var card = el('article', 'gcseo-trk-res');
    var head = el('header', 'gcseo-trk-res__head');
    var eyebrow = [];
    if (rec.o) eyebrow.push((T.order || 'Order') + ' ' + rec.o);
    if (rec.placed) eyebrow.push((T.placedOn || 'Placed') + ' ' + fd(rec.placed));
    if (byTracking && rec.n_items) eyebrow.push(count(rec.n_items, T.piecesOne || '1 piece in this package', T.piecesMany || '[n] pieces in this package'));
    if (eyebrow.length) head.appendChild(el('p', 'gcseo-eyebrow', eyebrow.join(' · ')));
    var h = el('h3', 'gcseo-trk-res__h', T.head[state] || state);
    h.tabIndex = -1;
    head.appendChild(h);

    if (state === 'preparing') {
      line(head, 'gcseo-trk-res__sub', rec.personalised ? T.sub.preparingPersonal : T.sub.preparing);
      if (rec.dispatch) line(head, 'gcseo-trk-res__eta', fill(T.dispatch, { range: range(rec.dispatch) }));
      if (rec.eta) line(head, 'gcseo-trk-res__eta', fill(T.eta, { range: range(rec.eta) }));
    } else if (state === 'overdue' || state === 'cancelled' || state === 'returned') {
      line(head, 'gcseo-trk-res__sub', T.sub[state]);
    } else if (state === 'shipped') {
      line(head, 'gcseo-trk-res__sub', fill(T.sub.shipped, { date: fd(lead.shipped) }));
    } else if (state === 'delivered') {
      var last = parcels.filter(function (p) { return p.delivered; }).sort(function (a, b) { return a.delivered < b.delivered ? -1 : 1; }).pop();
      line(head, 'gcseo-trk-res__sub', deliveredLine(last));
    } else {
      line(head, 'gcseo-trk-res__sub', T.sub[state]);
      line(head, 'gcseo-trk-res__eta', etaLine(lead));
    }
    if (state !== 'cancelled') line(head, 'gcseo-trk-note', noteFor(lead));
    if (lead && unshipped.length) {
      var n = unshipped.reduce(function (s, i) { return s + ((items[i] && items[i].q) || 1); }, 0);
      line(head, 'gcseo-trk-res__sub', count(n, T.partlyOne, T.partlyMany));
    }
    card.appendChild(head);

    if (state === 'cancelled') {
      out.appendChild(card);
      h.focus({ preventScroll: true });
      reveal();
      return;
    }

    if (!parcels.length) {
      var sec0 = el('section', 'gcseo-trk-parcel');
      sec0.appendChild(steps(null, rec.placed));
      if (items.length) sec0.appendChild(itemsList(items));
      card.appendChild(sec0);
    }
    parcels.forEach(function (p, k) {
      var sec = el('section', 'gcseo-trk-parcel');
      if (parcels.length > 1) {
        sec.appendChild(el('h4', 'gcseo-trk-parcel__h', (T.parcel || 'Package') + ' ' + (k + 1) + ' ' + (T.of || 'of') + ' ' + parcels.length));
        line(sec, 'gcseo-trk-parcel__st', T.head[shown(p)] || p.state);
        line(sec, 'gcseo-trk-parcel__eta', p.state === 'delivered' ? deliveredLine(p) : shown(p) === 'shipped' ? fill(T.sub.shipped, { date: fd(p.shipped) }) : etaLine(p));
        if (p !== lead) line(sec, 'gcseo-trk-note', noteFor(p));
      }
      sec.appendChild(steps(p, rec.placed));
      if (p.state === 'transit' && p.last && !p.untracked) line(sec, 'gcseo-note gcseo-trk-last', fill(T.lastUpdate, { date: fd(p.last) }));
      if (p.items && p.items.length && items.length) sec.appendChild(itemsList(items, p.items));
      if (T.showCarrier && p.carrier && p.tn4) line(sec, 'gcseo-note gcseo-trk-carrier', fill(T.carrier, { carrier: p.carrier, number: p.tn4 }));
      card.appendChild(sec);
    });
    if (parcels.length && unshipped.length) {
      var rest = el('section', 'gcseo-trk-parcel');
      rest.appendChild(el('h4', 'gcseo-trk-parcel__h', rec.overdue ? T.head.overdue : T.head.preparing));
      if (rec.overdue) line(rest, 'gcseo-trk-note', T.sub.overdue);
      else if (rec.dispatch) line(rest, 'gcseo-trk-parcel__eta', fill(T.dispatch, { range: range(rec.dispatch) }));
      rest.appendChild(itemsList(items, unshipped));
      card.appendChild(rest);
    }
    line(card, 'gcseo-note gcseo-trk-updated', T.updated);
    out.appendChild(card);
    h.focus({ preventScroll: true });
    reveal();
  }

  // Bring the result into view below the floating header (its height is the CSS scroll margin).
  function reveal() {
    var hdr = parseFloat(getComputedStyle(out).scrollMarginTop) || 0;
    var top = out.getBoundingClientRect().top;
    if (top < hdr || top > window.innerHeight * 0.7) {
      out.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  }

  // ---------------------------------------------------------------- forms
  var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab]'));
  var forms = { order: root.querySelector('[data-form="order"]'), tracking: root.querySelector('[data-form="tracking"]') };
  // Inputs carry data-field, not name: a native submit could never put them in a URL.
  function field(form, k) { return form.querySelector('[data-field="' + k + '"]'); }
  var fOrder = field(forms.order, 'order'), fEmail = field(forms.order, 'email'), fTracking = field(forms.tracking, 'tracking');
  function show(tab) {
    tabs.forEach(function (b) {
      var on = b.getAttribute('data-tab') === tab;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    Object.keys(forms).forEach(function (k) { forms[k].hidden = k !== tab; });
  }
  tabs.forEach(function (b, i) {
    b.addEventListener('click', function () { show(b.getAttribute('data-tab')); });
    b.addEventListener('keydown', function (e) {
      var j = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
      if (j == null) return;
      e.preventDefault();
      var next = tabs[(j + tabs.length) % tabs.length];
      show(next.getAttribute('data-tab'));
      next.focus();
    });
  });

  var busy = false;
  function run(identifier, form) {
    if (busy) return;
    busy = true;
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    out.setAttribute('aria-busy', 'true');
    loading();
    var byTracking = identifier.charAt(0) === 't';
    lookup(identifier).then(function (rec) {
      if (!rec) message(T.notFound, 'warn');
      else render(rec, byTracking);
    }).catch(function () {
      message(T.error, 'warn');
    }).then(function () {
      busy = false;
      if (btn) btn.removeAttribute('aria-busy');
      out.removeAttribute('aria-busy');
    });
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'gcseo_track_lookup', lookup_type: byTracking ? 'tracking' : 'order' }); } catch (e) {}
  }

  forms.order.addEventListener('submit', function (e) {
    e.preventDefault();
    if (busy) return;
    var o = normOrder(fOrder.value), m = normEmail(fEmail.value);
    if (!/^[A-Z0-9-]{3,20}$/.test(o)) { message(T.badOrder, 'warn'); fOrder.focus(); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m)) { message(T.badEmail, 'warn'); fEmail.focus(); return; }
    run('o|' + o + '|' + m, forms.order);
  });
  forms.tracking.addEventListener('submit', function (e) {
    e.preventDefault();
    if (busy) return;
    var t = normTracking(fTracking.value);
    if (!/^[A-Z0-9]{8,40}$/.test(t)) { message(T.badTracking, 'warn'); fTracking.focus(); return; }
    run('t|' + t, forms.tracking);
  });
  root.querySelectorAll('.gcseo-trk__go[disabled]').forEach(function (b) { b.disabled = false; });

  // ?order=1234 pre-fills the form (order emails can link here). Tracking numbers are never read from
  // the URL: they would end up in analytics, referrers and browser history.
  var qs = new URLSearchParams(location.search);
  if (qs.get('order')) {
    fOrder.value = qs.get('order').slice(0, 20);
    fEmail.focus();
  }
})();
